import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DOCUMENT_ENDPOINTS, STORAGE_KEYS, API_BASE_URL } from '../constants/api';
import { Document } from '../types/Document';
import { UserDetails, Agency } from '../types/User';

// Manages user-specific document data and actions (fetch, add, remove, update, upload, delete, field values, reminders).

export const useUserDocuments = (selectedUser: UserDetails | null, agency: Agency | null, documentFields?: { id: string }[]) => {
	const [userDocuments, setUserDocuments] = useState<Document[]>([]);
	const [userFieldValues, setUserFieldValues] = useState<{ [key: string]: any }>({});
	const [userReminders, setUserReminders] = useState<{ documentReminders: any[]; fieldReminders: any[] }>({ documentReminders: [], fieldReminders: [] });
	const [agencyReminders, setAgencyReminders] = useState<{ documentReminders: any[]; fieldReminders: any[] } | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Add local document management functions
	const addDocument = useCallback((newDoc: Document) => {
		setUserDocuments(prev => [...prev, newDoc]);
	}, []);

	const removeDocument = useCallback((documentId: string) => {
		setUserDocuments(prev => prev.filter(doc => doc.id !== documentId));
	}, []);

	const documentRemove = useCallback(async (docID: string) => {
		try {
			setLoading(true);
			const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
			await axios.post(DOCUMENT_ENDPOINTS.DELETE, {
				docID
			}, {
				headers: {
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				}
			});
			setUserDocuments(prev => prev.filter(doc => doc.id !== docID));
			return { success: true };
		} catch (error) {
			console.error('Delete failed:', error);
			return { success: false, error: error instanceof Error ? error.message : 'Delete failed' };
		} finally {
			setLoading(false);
		}
	}, []);

	const updateDocument = useCallback((documentId: string, updates: Partial<Document>) => {
		setUserDocuments(prev => prev.map(doc =>
			doc.id === documentId ? { ...doc, ...updates } : doc
		));
	}, []);


	// Fetch only documents
	const fetchUserDocuments = useCallback(async () => {
		const userId = selectedUser?.id;
		if (!userId || !agency?.isPaymentValid) {
			setUserDocuments([]);
			setLoading(false);
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const documentsResponse = await axios.get(
				DOCUMENT_ENDPOINTS.USER_DOCUMENTS(userId),
				{ headers }
			);
			const documents = documentsResponse.data || [];
			setUserDocuments(documents);
		} catch (err: any) {
			setError('Failed to fetch user documents');
			setUserDocuments([]);
		} finally {
			setLoading(false);
		}
	}, [selectedUser?.id, agency?.isPaymentValid]);

	// Fetch all field values for all field IDs
	const fetchUserFields = useCallback(async () => {
		const userId = selectedUser?.id;
		const agencyId = agency?.agencyId;
		if (!userId || !agencyId || !agency?.isPaymentValid || !documentFields || documentFields.length === 0) {
			setUserFieldValues({});
			return;
		}
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const response = await axios.post(
				DOCUMENT_ENDPOINTS.FIELD_VALUES_BY_IDS,
				{
					userID: userId,
					agencyID: agencyId,
					fieldIDs: documentFields.map(f => f.id)
				},
				{ headers }
			);
			const values: { [key: string]: any } = {};
			(response.data || []).forEach((item: any) => {
				values[item.fieldID] = item.fieldValue || item.value;
			});
			setUserFieldValues(values);
		} catch (err: any) {
			setError('Failed to fetch field values');
			setUserFieldValues({});
		}
	}, [selectedUser?.id, agency?.agencyId, agency?.isPaymentValid, documentFields]);

	// Fetch both documents and fields
	const fetchUserData = useCallback(async () => {
		await fetchUserDocuments();
		// Note: Field values are fetched separately in useEffect when documentFields changes
	}, [fetchUserDocuments]);

	const fetchFieldValues = useCallback(async (fieldIds: string[]) => {
		const userId = selectedUser?.id;
		const agencyId = agency?.agencyId;
		if (!userId || !agencyId || fieldIds.length === 0) {
			return;
		}
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			
			// Always use POST to avoid URL length limits with many field IDs
			const response = await axios.post(
				DOCUMENT_ENDPOINTS.FIELD_VALUES_BY_IDS,
				{
					userID: userId,
					agencyID: agencyId,
					fieldIDs: fieldIds
				},
				{ headers }
			);
			
			const values: { [key: string]: any } = {};
			(response.data || []).forEach((item: any) => {
				values[item.fieldID] = item.fieldValue || item.value;
			});
			setUserFieldValues(prev => ({ ...prev, ...values }));
		} catch (err: any) {
			setError('Failed to fetch field values');
		}
	}, [selectedUser?.id, agency?.agencyId]);

	useEffect(() => {
		fetchUserData();
	}, [fetchUserData]);

	// Fetch field values when documentFields changes
	useEffect(() => {
		if (selectedUser?.id && agency?.agencyId && agency?.isPaymentValid && documentFields && documentFields.length > 0) {
			fetchUserFields();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUser?.id, agency?.agencyId, agency?.isPaymentValid, documentFields]);

	// Fetch user reminders when selectedUser changes
	useEffect(() => {
		if (selectedUser?.id && agency?.isPaymentValid) {
			fetchUserReminders(selectedUser.id);
		} else {
			setUserReminders({ documentReminders: [], fieldReminders: [] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUser?.id, agency?.isPaymentValid]);

	// Fetch agency reminders when agency changes
	useEffect(() => {
		if (agency?.agencyId && agency?.isPaymentValid) {
			fetchAgencyReminders(agency.agencyId);
		} else {
			setAgencyReminders(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [agency?.agencyId, agency?.isPaymentValid]);

	const saveFieldValues = useCallback(async (values: { [key: string]: any }) => {
		const userID = selectedUser?.id;
		const agencyID = agency?.agencyId;
		if (!userID || !agencyID) {
			return { success: false, error: 'Missing user or agency ID' };
		}
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			await axios.post(
				DOCUMENT_ENDPOINTS.FIELD_VALUES_SAVE,
				{
					values,
					userID,
					agencyID,
				},
				{ headers }
			);
			// Merge into existing values to avoid wiping others
			setUserFieldValues(prev => ({ ...prev, ...values }));
			return { success: true, error: null };
		} catch (err: any) {
			return { success: false, error: 'Failed to save field values' };
		}
	}, [selectedUser?.id, agency?.agencyId]);

	const documentUpload = useCallback(async (formData: FormData) => {
		try {
			setLoading(true);
			const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
			const response = await axios.post(DOCUMENT_ENDPOINTS.UPLOAD, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
			});

			// Use context's optimistic update instead of local state
			const newDocument = response.data;
			if (newDocument && addDocument) {
				addDocument(newDocument);
			}

			return { success: true, newDocument };
		} catch (err: any) {
			console.error('Upload failed:', err);
			const message = err?.response?.data?.error || err?.message || 'Upload failed';
			return { success: false, error: message };
		} finally {
			setLoading(false);
		}
	}, [addDocument]);

	const documentDownload = useCallback(async (docID: string) => {
		try {
			const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
			if (!token) throw new Error('No auth token found');

			const headers = {
				Authorization: `Bearer ${token}`,
			};

			// Make request for the file as a blob
			const response = await axios.get(
				DOCUMENT_ENDPOINTS.DOWNLOAD(docID),
				{ headers, responseType: 'blob' }
			);

			// 1. Try to get filename from Content-Disposition header
			let fileName = 'document';
			const disposition = response.headers['content-disposition'];
			if (disposition && disposition.indexOf('filename=') !== -1) {
				fileName = disposition
					.split('filename=')[1]
					.replace(/['"]/g, '')
					.trim();
			}

			// 2. If filename has no extension, infer from blob type
			if (!/\.[a-z0-9]+$/i.test(fileName)) {
				const mimeToExt: { [key: string]: string } = {
					'application/pdf': '.pdf',
					'application/msword': '.doc',
					'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
					'image/png': '.png',
					'image/jpeg': '.jpg',
					'text/plain': '.txt',
					// add more as needed
				};
				const ext = mimeToExt[response.data.type];
				if (ext && !fileName.endsWith(ext)) {
					fileName += ext;
				}
			}

			// 3. Use the filename in the download attribute
			const url = window.URL.createObjectURL(response.data);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Download failed:', error);
		}
	}, []);

	const fetchRecentDocs = useCallback(async (agentID: string) => {
		if (!agentID) return;
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const response = await axios.get(
				`${API_BASE_URL}/api/documents/recent/${agentID}`,
				{ headers }
			);
			const recentDocs = response.data || [];
			return recentDocs;
			// Handle recentDocs as needed, e.g., set to state
		} catch (err: any) {
			console.error('Failed to fetch recent documents');
		}
	}, []);

	const fetchUserReminders = useCallback(async (userID: string) => {
		const empty = { documentReminders: [], fieldReminders: [] };
		if (!userID) return empty;
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const response = await axios.get(
				`${API_BASE_URL}/api/reminders/get_reminders/${userID}`,
				{ headers }
			);
			const reminders = response.data || empty;
			setUserReminders(reminders);
			return reminders;
		} catch (err: any) {
			console.error('Failed to fetch user reminders');
			return empty;
		}
	}, []);

	const fetchAgencyReminders = useCallback(async (agencyID: string) => {
		if (!agencyID) return { documentReminders: [], fieldReminders: [] };
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const response = await axios.get(
				`${API_BASE_URL}/api/reminders/get_agency_reminders/${agencyID}`,
				{ headers }
			);
			const data = response.data || { documentReminders: [], fieldReminders: [] };
			setAgencyReminders(data);
			return data;
		} catch (err: any) {
			console.error('Failed to fetch agency reminders');
			return { documentReminders: [], fieldReminders: [] };
		}
	}, []);

	const removeUserDocumentReminders = useCallback(async (documentTypeID: string, userID: string) => {
		if (!userID) return;
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const response = await axios.post(
				`${API_BASE_URL}/api/reminders/remove_document_reminders`,
				{ documentTypeID, userID }, // POST body
				{ headers } // config (headers, etc)
			);
			if (response.data.success && !response.data.error) {
				const success = response.data || null;
				return success;
			} else if (response.data.error) {
				const error = response.data.error || null;
				return error;
			}
		} catch (err: any) {
			console.error('Failed to remove user reminders');
		}
	}, []);

	const removeUserFieldReminders = useCallback(async (fieldTypeID: string, userID: string) => {
		if (!userID) return;
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const response = await axios.post(
				`${API_BASE_URL}/api/reminders/remove_field_reminders`,
				{ fieldTypeID, userID }, // POST body
				{ headers } // config (headers, etc)
			);
			if (response.data.success && !response.data.error) {
				const success = response.data || null;
				return success;
			} else if (response.data.error) {
				const error = response.data.error || null;
				return error;
			}
		} catch (err: any) {
			console.error('Failed to remove user reminders');
		}
	}, []);

	const sendUserDocumentReminders = useCallback(async (documentTypeID: string, userID: string, managerID: string, text: string) => {
		if (!userID) return;
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const response = await axios.post(
				`${API_BASE_URL}/api/reminders/send_document_reminder`,
				{ documentTypeID, userID, managerID, text }, // POST body
				{ headers } // config (headers, etc)
			);
			const reminders = response.data || [];
			setUserReminders(reminders);
			return reminders;
		} catch (err: any) {
			console.error('Failed to fetch user reminders');
		}
	}, []);

	const sendUserFieldReminders = useCallback(async (fieldTypeID: string, userID: string, managerID: string, text: string) => {
		if (!userID) return;
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			const response = await axios.post(
				`${API_BASE_URL}/api/reminders/send_field_reminder`,
				{ fieldTypeID: fieldTypeID, userID, managerID, text }, // POST body
				{ headers } // config (headers, etc)
			);
			const reminders = response.data || [];
			setUserReminders(reminders);
			return reminders;
		} catch (err: any) {
			console.error('Failed to fetch user reminders');
		}
	}, []);

	return {
		userDocuments,
		userFieldValues,
		loading,
		error,
		refetch: fetchUserData,
		fetchFieldValues,
		saveFieldValues,
		// Local document management functions for optimistic updates
		addDocument,
		removeDocument,
		updateDocument,
		documentDownload,
		documentUpload,
		documentRemove,
		fetchRecentDocs,
		fetchUserReminders,
		sendUserDocumentReminders,
		sendUserFieldReminders,
		userReminders,
		agencyReminders,
		removeUserDocumentReminders,
		removeUserFieldReminders,
		fetchAgencyReminders
	};
};
